import { formatAmount } from "../../include/money";
import { useCallback, useEffect, useState } from "react";
import { Box, Button, MenuItem, TextField, Typography } from "@mui/material";
import { Add } from "@mui/icons-material";
import { QRCodeCanvas } from "qrcode.react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { adminPost } from "./adminApi";
import { consoleColors, primaryButtonSx } from "./consoleTheme";
import { indiaDate } from "../../include/indiaDate";
import { LaunchChooser, LaunchTimeFields, resizeLaunchTimes } from "./LaunchTimes";

const emptyForm = {
  draw_id: "",
  title: "",
  frequency: "hour",
  start_at: "",
  end_at: "",
  min_sale_amount: "",
  print_no: "",
  background_image: "",
  back_image: "",
  banner: "",
  scope: "club",
  club_id: "",
  store_id: "",
};

function toLocal(value) {
  return String(value || "").replace(" ", "T").slice(0, 16);
}

function readImage(file) {
  return new Promise((resolve, reject) => {
    if (!file) return resolve("");
    if (file.size > 900000) return reject(new Error("Use an image under 900 KB"));
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Could not read the image"));
    reader.readAsDataURL(file);
  });
}

export default function AdminDraws() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [draws, setDraws] = useState([]);
  const [chooser, setChooser] = useState(null);

  const load = useCallback(async () => {
    const result = await adminPost("DRAW_ADMIN_LIST");
    if (result.status === "200") setDraws(result.data?.draws || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (params.get("create") === "1") {
      const clubId = params.get("club_id") || "";
      navigate(clubId ? `/console/luckydraw/new?club_id=${clubId}` : "/console/luckydraw/new", { replace: true });
    }
  }, [params, navigate]);

  const toggle = async (draw) => {
    const next = draw.status === "1" ? "0" : "1";
    const result = await adminPost("DRAW_STATUS", { draw_id: draw.draw_id, status: next });
    if (result.status === "200") {
      toast.success(result.data?.message);
      load();
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 1, flexWrap: "wrap" }}>
        <Typography sx={{ color: consoleColors.muted, fontSize: 14 }}>A club draw appears in every store in that club. A store draw appears in one store.</Typography>
        <Button variant="contained" sx={primaryButtonSx} onClick={() => navigate("/console/luckydraw/new")}><Add sx={{ mr: 0.5 }} /> Create Lucky Draw Coupons</Button>
      </Box>

      {draws.length === 0 && <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2, p: 4, textAlign: "center", color: consoleColors.muted }}>No Lucky Draw Coupons yet.</Box>}

      <Box sx={{ display: "grid", gap: 1.5 }}>
        {draws.map((draw) => (
          <Box key={draw.draw_id} sx={{ background: draw.status === "1" ? "#fff" : "#f6eaea", border: `1px solid ${draw.status === "1" ? consoleColors.line : "#e7cfcf"}`, borderRadius: 2.5, p: 2, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
            <QRCodeCanvas value={draw.draw_id} size={72} />
            <Box sx={{ flex: 1, minWidth: 220 }}>
              <Typography sx={{ fontSize: 18, fontWeight: 700 }}>{draw.title}</Typography>
              <Typography sx={{ fontSize: 13, color: consoleColors.muted, mt: 0.4 }}>{draw.scope === "club" ? `Club · ${draw.club_title || draw.club_code}` : `Store · ${draw.store_name}`} · {draw.frequency_label}</Typography>
              <Typography sx={{ fontSize: 13, color: consoleColors.ink, mt: 0.4 }}>{indiaDate(draw.start_at)} to {indiaDate(draw.end_at)} · Draw {draw.print_no ? Number(draw.print_no) : "-"} · Minimum sale ₹{formatAmount(draw.min_sale_amount)} · {draw.issued_count || 0} issued · {draw.winner_count || 0} winners · {draw.status === "1" ? "Enabled" : "Disabled"}</Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button size="small" variant="contained" sx={primaryButtonSx} onClick={() => setChooser(draw)}>Launch draw</Button>
              <Button size="small" sx={{ textTransform: "none" }} onClick={() => navigate(`/console/luckydraw/${draw.draw_id}/edit`)}>Edit</Button>
              <Button size="small" sx={{ textTransform: "none" }} onClick={() => toggle(draw)}>{draw.status === "1" ? "Disable" : "Enable"}</Button>
              <Button size="small" sx={{ textTransform: "none" }} onClick={() => navigate(`/console/luckydraw/${draw.draw_id}?view=result`)}>Result</Button>
              <Button size="small" sx={{ textTransform: "none" }} onClick={() => navigate(`/console/luckydraw/${draw.draw_id}?view=issued`)}>Issued</Button>
              <Button size="small" sx={{ textTransform: "none" }} onClick={() => navigate(`/console/luckydraw/${draw.draw_id}?view=winners`)}>Winners</Button>
            </Box>
          </Box>
        ))}
      </Box>
      <LaunchChooser
        open={Boolean(chooser)}
        title={chooser?.title || ""}
        frequencyLabel={chooser?.frequency_label}
        launches={chooser?.launches || []}
        loadSlots={(resultMode) => adminPost("DRAW_SLOTS", { draw_id: chooser.draw_id, result_mode: resultMode })}
        onClose={() => setChooser(null)}
        onOpen={(slot) => navigate(`/console/luckydraw/${chooser.draw_id}/launch?slot=${slot.index}`)}
        onOpenFrequency={(resultMode, slot) => navigate(`/console/luckydraw/${chooser.draw_id}/launch?mode=${resultMode}&key=${encodeURIComponent(slot.key)}`)}
        onShare={async (payload) => {
          const result = await adminPost("LAUNCH_LINK", { kind: "coupon", draw_id: chooser.draw_id, ...payload });
          if (result.status !== "200") {
            toast.info(result.status_message || result.data?.error || "Could not make the launch link");
            return "";
          }
          return `${window.location.origin}${result.data.path}`;
        }}
      />
    </Box>
  );
}

export function DrawForm() {
  const { drawId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({ ...emptyForm, club_id: params.get("club_id") || "", launch_count: "1", launch_times: [""], launch_locked: [] });
  const [clubs, setClubs] = useState([]);
  const [stores, setStores] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const [clubResult, storeResult] = await Promise.all([adminPost("CLUB_LIST"), adminPost("CLUB_STORE_OPTIONS")]);
      if (!mounted) return;
      if (clubResult.status === "200") setClubs(clubResult.data?.clubs || []);
      if (storeResult.status === "200") setStores(storeResult.data?.stores || []);
      if (!drawId) return;
      const result = await adminPost("DRAW_DETAIL", { draw_id: drawId });
      if (!mounted || result.status !== "200") return;
      const full = result.data?.draw || {};
      setForm({
        draw_id: full.draw_id || drawId,
        title: full.title || "",
        frequency: full.frequency || "hour",
        start_at: toLocal(full.start_at),
        end_at: toLocal(full.end_at),
        min_sale_amount: full.min_sale_amount ?? "",
        print_no: full.print_no || "",
        background_image: full.background_image || "",
        back_image: full.back_image || "",
        banner: full.banner || "",
        scope: full.scope === "store" ? "store" : "club",
        club_id: full.club_id || "",
        store_id: full.store_id || "",
        launch_count: String(Math.max(1, (full.launches || []).length || 1)),
        launch_times: resizeLaunchTimes((full.launches || []).map((slot) => toLocal(slot.at)), Math.max(1, (full.launches || []).length || 1)),
        launch_locked: (full.launches || []).map((slot) => slot.launched === "1"),
      });
    })();
    return () => { mounted = false; };
  }, [drawId]);

  const onFile = async (key, file) => {
    try {
      const image = await readImage(file);
      setForm((prev) => ({ ...prev, [key]: image }));
    } catch (error) {
      toast.error(error.message);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const result = await adminPost("DRAW_SAVE", { ...form, min_sale_amount: Number(form.min_sale_amount || 0), launch_times: form.launch_times });
      if (result.status === "200") {
        toast.success(result.data?.message || "Saved");
        navigate("/console/luckydraw");
      } else toast.info(result.status_message || result.data?.error || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 760 }}>
      <Button sx={{ textTransform: "none", mb: 2 }} onClick={() => navigate("/console/luckydraw")}>Back to Lucky Draw Coupons</Button>
      <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, p: { xs: 2, md: 3 }, display: "grid", gap: 1.5 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 750 }}>{drawId ? "Edit Lucky Draw Coupons" : "Create Lucky Draw Coupons"}</Typography>
        <TextField label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <TextField select label="Lucky Draw Frequency" value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })}>
          <MenuItem value="hour">Every 1 hour</MenuItem>
          <MenuItem value="day">Every day</MenuItem>
          <MenuItem value="all">All entries</MenuItem>
        </TextField>
        <TextField label="Starts" type="datetime-local" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} InputLabelProps={{ shrink: true }} />
        <TextField label="Ends" type="datetime-local" value={form.end_at} onChange={(e) => setForm({ ...form, end_at: e.target.value })} InputLabelProps={{ shrink: true }} />
        <TextField label="Minimum sale amount" type="number" value={form.min_sale_amount} onChange={(e) => setForm({ ...form, min_sale_amount: e.target.value })} helperText="A coupon is issued when the sale is at least this amount." />
        <TextField label="Draw number" value={form.print_no} onChange={(e) => setForm({ ...form, print_no: e.target.value.replace(/\D/g, "").slice(0, 2) })} helperText="1, 2, 3… printed in the coupon barcode. Leave blank to assign the next number." inputProps={{ inputMode: "numeric", maxLength: 2 }} />
        <LaunchTimeFields
          count={form.launch_count}
          times={form.launch_times}
          locked={form.launch_locked}
          onCount={(value) => {
            const count = Math.min(30, Math.max(1, Number(value) || 1));
            const kept = (form.launch_locked || []).filter(Boolean).length;
            if (count < kept) return;
            setForm({ ...form, launch_count: String(count), launch_times: resizeLaunchTimes(form.launch_times, count) });
          }}
          onTime={(index, value) => {
            const launch_times = form.launch_times.slice();
            launch_times[index] = value;
            setForm({ ...form, launch_times });
          }}
        />
        <TextField select label="Show in" value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })}>
          <MenuItem value="club">Club</MenuItem>
          <MenuItem value="store">Single store</MenuItem>
        </TextField>
        {form.scope === "club" ? (
          <TextField select label="Club" value={form.club_id} onChange={(e) => setForm({ ...form, club_id: e.target.value })}>
            {clubs.map((club) => <MenuItem key={club.club_id} value={club.club_id}>{club.title} · {club.code}</MenuItem>)}
          </TextField>
        ) : (
          <TextField select label="Store" value={form.store_id} onChange={(e) => setForm({ ...form, store_id: e.target.value })}>
            {stores.map((store) => <MenuItem key={store.store_id} value={store.store_id}>{store.store_name}</MenuItem>)}
          </TextField>
        )}
        <Button component="label" sx={{ textTransform: "none", justifyContent: "flex-start" }}>
          Front image {form.background_image ? "· selected" : ""}
          <input hidden type="file" accept="image/*" onChange={(e) => onFile("background_image", e.target.files?.[0])} />
        </Button>
        {form.background_image && <Box component="img" src={form.background_image} alt="Front" sx={{ width: "100%", height: 90, objectFit: "cover", borderRadius: 1 }} />}
        <Button component="label" sx={{ textTransform: "none", justifyContent: "flex-start" }}>
          Back image (optional) {form.back_image ? "· selected" : ""}
          <input hidden type="file" accept="image/*" onChange={(e) => onFile("back_image", e.target.files?.[0])} />
        </Button>
        {form.back_image && <Box component="img" src={form.back_image} alt="Back" sx={{ width: "100%", height: 90, objectFit: "cover", borderRadius: 1 }} />}
        <Button component="label" sx={{ textTransform: "none", justifyContent: "flex-start" }}>
          Banner {form.banner ? "· selected" : ""}
          <input hidden type="file" accept="image/*" onChange={(e) => onFile("banner", e.target.files?.[0])} />
        </Button>
        {form.banner && <Box component="img" src={form.banner} alt="Banner" sx={{ width: "100%", height: 90, objectFit: "cover", borderRadius: 1 }} />}
        {form.draw_id && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <QRCodeCanvas value={form.draw_id} size={84} />
            <Typography sx={{ fontSize: 13, color: consoleColors.muted }}>QR for this draw. Code {form.draw_id}</Typography>
          </Box>
        )}
        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <Button onClick={() => navigate("/console/luckydraw")} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button variant="contained" sx={primaryButtonSx} disabled={saving} onClick={save}>{saving ? "Saving..." : "Save"}</Button>
        </Box>
      </Box>
    </Box>
  );
}
